import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { niciApi } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { NiciListino, NiciListinoVoce } from '../../types/nici'
import toast from 'react-hot-toast'
import { FileSpreadsheet, Upload, Download, Pencil, Check, X } from 'lucide-react'

const RUOLI_LISTINI_MANAGE = ['superadmin', 'admin']

function TabellaVoci({ listino, editabile }: { listino: NiciListino; editabile: boolean }) {
  const qc = useQueryClient()
  const [editId, setEditId] = useState<string | null>(null)
  const [prezzoBozza, setPrezzoBozza] = useState('')

  const { data: voci = [], isLoading } = useQuery({
    queryKey: ['nici-listino-voci', listino.id],
    queryFn: () => niciApi.listini.voci.list(listino.id).then(r => r.data as NiciListinoVoce[]),
  })

  const updateMut = useMutation({
    mutationFn: (v: { voceId: string; prezzo_unitario: number }) =>
      niciApi.listini.voci.update(listino.id, v.voceId, { prezzo_unitario: v.prezzo_unitario }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['nici-listino-voci', listino.id] })
      setEditId(null)
      toast.success('Prezzo aggiornato')
    },
    onError: () => toast.error('Errore aggiornamento prezzo'),
  })

  const importMut = useMutation({
    mutationFn: (file: File) => niciApi.listini.importExcel('COSTI_INTERNO', listino.id, file),
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: ['nici-listino-voci', listino.id] })
      const { importate, errori } = resp.data as { importate: number; errori: string[] }
      if (errori.length) toast.error(`${importate} righe importate, ${errori.length} errori: ${errori[0]}`)
      else toast.success(`${importate} righe importate`)
    },
    onError: () => toast.error('Errore import Excel'),
  })

  if (isLoading) return <div className="card p-8 text-center text-sm text-slate-400">Caricamento...</div>

  return (
    <div className="card overflow-x-auto">
      {editabile && listino.tipo === 'COSTI_INTERNO' && (
        <div className="p-3 flex items-center gap-2 border-b border-slate-100">
          <label className="btn-secondary flex items-center gap-2 cursor-pointer text-sm">
            <Upload size={14} /> Importa Excel
            <input type="file" accept=".xlsx" className="hidden" onChange={e => {
              const file = e.target.files?.[0]
              if (file) importMut.mutate(file)
              e.target.value = ''
            }} />
          </label>
          <button
            className="btn-ghost flex items-center gap-2 text-sm"
            onClick={async () => {
              const resp = await niciApi.listini.templateCosti()
              const url = URL.createObjectURL(resp.data as Blob)
              const a = document.createElement('a')
              a.href = url; a.download = 'template_costi_nici.xlsx'; a.click()
              URL.revokeObjectURL(url)
            }}
          >
            <Download size={14} /> Template
          </button>
        </div>
      )}
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
            <th className="p-2">NMU</th><th className="p-2">Descrizione</th><th className="p-2">Gruppo</th>
            <th className="p-2">UM</th><th className="p-2 text-right">Prezzo</th>
            {editabile && <th className="p-2 w-16"></th>}
          </tr>
        </thead>
        <tbody>
          {voci.map(v => (
            <tr key={v.id} className="border-b border-slate-50">
              <td className="p-2 font-mono text-xs">{v.nmu_code || '—'}</td>
              <td className="p-2">{v.descrizione}</td>
              <td className="p-2 text-xs text-slate-500">{v.gruppo}</td>
              <td className="p-2 text-xs">{v.um}</td>
              <td className="p-2 text-right">
                {editId === v.id ? (
                  <input className="input text-right w-24 py-1" value={prezzoBozza}
                    onChange={e => setPrezzoBozza(e.target.value)} autoFocus />
                ) : (
                  <>€ {v.prezzo_unitario.toFixed(2)}</>
                )}
              </td>
              {editabile && (
                <td className="p-2">
                  {editId === v.id ? (
                    <div className="flex gap-1">
                      <button className="btn-ghost p-1" onClick={() => {
                        const val = parseFloat(prezzoBozza.replace(',', '.'))
                        if (Number.isNaN(val)) { toast.error('Prezzo non valido'); return }
                        updateMut.mutate({ voceId: v.id, prezzo_unitario: val })
                      }}><Check size={14} style={{ color: '#15803d' }} /></button>
                      <button className="btn-ghost p-1" onClick={() => setEditId(null)}>
                        <X size={14} style={{ color: '#dc2626' }} />
                      </button>
                    </div>
                  ) : (
                    (listino.tipo === 'COSTI_INTERNO') && (
                      <button className="btn-ghost p-1" onClick={() => { setEditId(v.id); setPrezzoBozza(String(v.prezzo_unitario)) }}>
                        <Pencil size={13} style={{ color: '#64748b' }} />
                      </button>
                    )
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function NiciListini() {
  const { user } = useAuth()
  const editabile = RUOLI_LISTINI_MANAGE.includes(user?.role ?? '')
  const [tab, setTab] = useState<'CAPITOLATO' | 'COSTI_INTERNO'>('CAPITOLATO')

  const { data: listini = [], isLoading } = useQuery({
    queryKey: ['nici-listini', tab],
    queryFn: () => niciApi.listini.list(tab).then(r => r.data as NiciListino[]),
  })

  const listinoAttivo = listini.find(l => l.attivo)

  return (
    <div className="space-y-6">
      <div>
        <h1 className="page-title">Listini NICI</h1>
        <p className="page-subtitle">Listino capitolato (ricavi) e listino costi interno</p>
      </div>

      <div className="flex gap-2 border-b border-slate-200">
        <button className={`px-4 py-2 text-sm font-medium ${tab === 'CAPITOLATO' ? 'border-b-2' : 'text-slate-500'}`}
          style={tab === 'CAPITOLATO' ? { borderColor: '#1E40AF', color: '#1E40AF' } : undefined}
          onClick={() => setTab('CAPITOLATO')}>
          Capitolato
        </button>
        <button className={`px-4 py-2 text-sm font-medium ${tab === 'COSTI_INTERNO' ? 'border-b-2' : 'text-slate-500'}`}
          style={tab === 'COSTI_INTERNO' ? { borderColor: '#1E40AF', color: '#1E40AF' } : undefined}
          onClick={() => setTab('COSTI_INTERNO')}>
          Costi interni
        </button>
      </div>

      {isLoading ? (
        <div className="card p-8 text-center text-sm text-slate-400">Caricamento...</div>
      ) : !listinoAttivo ? (
        <div className="card p-12 text-center space-y-2">
          <FileSpreadsheet size={40} className="mx-auto" style={{ color: '#cbd5e1' }} />
          <p className="text-slate-500 font-medium">Nessun listino attivo di questo tipo</p>
        </div>
      ) : (
        <>
          <p className="text-sm text-slate-500">{listinoAttivo.nome} — valido dal {listinoAttivo.valido_dal}</p>
          <TabellaVoci listino={listinoAttivo} editabile={editabile} />
        </>
      )}
    </div>
  )
}
